import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { X } from 'lucide-react';
import { CHECKLIST_KEYS } from './RnQpDecisionPanel';
import RnQpCreateForm from './RnQpCreateForm';
import RnQpThreadCard from './RnQpThreadCard';
import { useRnQpRequests } from '../hooks/useRnQpRequests';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  rnWorkOrderId: string;
  rnWorkOrderNumber?: string;
}

const toISODate = (value: string): string | undefined => {
  const parsed = moment(value.trim(), 'DD.MM.YYYY', true);
  return parsed.isValid() ? parsed.format('YYYY-MM-DD') : undefined;
};
const defaultChecklist = () => CHECKLIST_KEYS.map((key) => ({ key, checked: false }));

const RnQpRequestModal: React.FC<Props> = ({ isOpen, onClose, rnWorkOrderId, rnWorkOrderNumber }) => {
  const { t } = useTranslation();
  const {
    threads,
    loading,
    nextNumber,
    page,
    totalPages,
    loadThreads,
    loadMore,
    refreshNextNumber,
    createThread,
    addMessage,
    decide,
  } = useRnQpRequests();

  const [subject, setSubject] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [recipients, setRecipients] = useState('');
  const [text, setText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [replyText, setReplyText] = useState<Record<string, string>>({});
  const [replyDateMode, setReplyDateMode] = useState<Record<string, 'accept' | 'new'>>({});
  const [replyDateValue, setReplyDateValue] = useState<Record<string, string>>({});
  const [checklists, setChecklists] = useState<Record<string, { key: string; checked: boolean }[]>>({});
  const [pin, setPin] = useState('');
  const [reason, setReason] = useState('');
  const [decisionTarget, setDecisionTarget] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && rnWorkOrderId) {
      void loadThreads(rnWorkOrderId);
      void refreshNextNumber(rnWorkOrderId);
    }
  }, [isOpen, rnWorkOrderId, loadThreads, refreshNextNumber]);

  useEffect(() => {
    if (!isOpen) {
      setSubject('');
      setDateTo('');
      setRecipients('');
      setText('');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const canSend = !!rnWorkOrderId && !!subject.trim() && !!recipients.trim() && !submitting;

  const handleSubmit = async () => {
    if (!canSend) return;
    setSubmitting(true);
    const created = await createThread({
      rnWorkOrder: rnWorkOrderId,
      subject: subject.trim(),
      recipients: Array.from(new Set(recipients.split(',').map((id) => id.trim()).filter(Boolean))),
      text: text.trim(),
      dateTo: toISODate(dateTo) || null,
    });
    setSubmitting(false);
    if (created) {
      setSubject('');
      setDateTo('');
      setRecipients('');
      setText('');
      void refreshNextNumber(rnWorkOrderId);
    }
  };

  const handleReply = async (id: string) => {
    const value = (replyText[id] || '').trim();
    if (!value) return;
    const mode = replyDateMode[id] || 'accept';
    const date = mode === 'new' ? toISODate(replyDateValue[id] || '') : undefined;
    if (mode === 'new' && !date) return;
    const updated = await addMessage(id, value, date);
    if (updated) {
      setReplyText((prev) => ({ ...prev, [id]: '' }));
      setReplyDateValue((prev) => ({ ...prev, [id]: '' }));
      setReplyDateMode((prev) => ({ ...prev, [id]: 'accept' }));
    }
  };

  const handleDecision = async (decision: 'approved' | 'rejected', id: string) => {
    const decisionChecklist = checklists[id] || defaultChecklist();
    if (decision === 'approved' && !decisionChecklist.every((item) => item.checked)) return;
    if (decision === 'rejected' && !reason.trim()) return;
    if (!pin.trim()) return;
    setSubmitting(true);
    const updated = await decide(id, { decision, pin: pin.trim(), reason: reason.trim(), checklist: decisionChecklist });
    setSubmitting(false);
    if (updated) {
      setDecisionTarget(null);
      setPin('');
      setReason('');
      setChecklists((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="flex max-h-[90vh] w-full max-w-3xl flex-col rounded-lg bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-200 px-5 py-3">
          <div className="min-w-0 pr-3">
            <div className="text-sm font-semibold text-blue-700">
              {nextNumber || (rnWorkOrderNumber ? `${rnWorkOrderNumber}/QP/01` : t('rnQpRequests.title'))}
            </div>
            <h2 className="truncate text-xs font-medium text-gray-600">{t('rnQpRequests.title')}</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus:bg-blue-50 focus:ring-2 focus:ring-blue-500/50"
            aria-label={t('rnQpRequests.close')}
          >
            <X size={18} />
          </button>
        </div>

        <div className="overflow-y-auto px-5 py-4 space-y-4">
          <RnQpCreateForm
            nextNumber={nextNumber}
            rnWorkOrderNumber={rnWorkOrderNumber}
            subject={subject}
            onSubjectChange={setSubject}
            dateTo={dateTo}
            onDateToChange={setDateTo}
            recipients={recipients}
            onRecipientsChange={setRecipients}
            text={text}
            onTextChange={setText}
            canSend={canSend}
            submitting={submitting}
            onSubmit={handleSubmit}
          />

          {loading && <div className="text-center text-xs text-gray-500">{t('common.loading') || 'Loading...'}</div>}
          {!loading && threads.length === 0 && (
            <div className="py-6 text-center text-xs text-gray-500">{t('rnQpRequests.noThreads')}</div>
          )}

          {threads.map((thread) => {
            const checklist = checklists[thread._id] || defaultChecklist();
            // PIN/reason belong to the thread they were typed in — never unlock another thread.
            const isTarget = decisionTarget === thread._id;
            const canApprove = isTarget && checklist.every((item) => item.checked) && !!pin && !submitting;
            const canReject = isTarget && !!reason.trim() && !!pin && !submitting;
            const displayPin = decisionTarget === thread._id ? pin : '';
            const displayReason = decisionTarget === thread._id ? reason : '';

            return (
              <RnQpThreadCard
                key={thread._id}
                thread={thread}
                replyText={replyText[thread._id] || ''}
                onReplyTextChange={(value) => setReplyText((prev) => ({ ...prev, [thread._id]: value }))}
                replyDateMode={replyDateMode[thread._id] || 'accept'}
                onReplyDateModeChange={(mode) => setReplyDateMode((prev) => ({ ...prev, [thread._id]: mode }))}
                replyDateValue={replyDateValue[thread._id] || ''}
                onReplyDateValueChange={(value) => setReplyDateValue((prev) => ({ ...prev, [thread._id]: value }))}
                onReply={() => void handleReply(thread._id)}
                checklist={checklist}
                onChecklistToggle={(key, checked) =>
                  setChecklists((prev) => ({
                    ...prev,
                    [thread._id]: (prev[thread._id] || defaultChecklist()).map((row) =>
                      row.key === key ? { ...row, checked } : row
                    ),
                  }))
                }
                pin={displayPin}
                reason={displayReason}
                onPinChange={(value) => {
                  setDecisionTarget(thread._id);
                  setPin(value);
                }}
                onReasonFocus={() => setDecisionTarget(thread._id)}
                onReasonChange={setReason}
                canApprove={canApprove}
                canReject={canReject}
                onApprove={() => void handleDecision('approved', thread._id)}
                onReject={() => void handleDecision('rejected', thread._id)}
              />
            );
          })}

          {page < totalPages && (
            <div className="flex justify-center">
              <button
                type="button"
                disabled={loading}
                onClick={() => void loadMore(rnWorkOrderId)}
                className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-blue-700 hover:bg-blue-50 disabled:opacity-50"
              >
                {loading ? '...' : t('rnQpRequests.loadMore')}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RnQpRequestModal;
